import React, { useState } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { apiGet, apiPost } from '../../../services/api';

export default function ReactivationFeeGate({ token, fee, onCleared }) {
  const [paying, setPaying] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [checkoutOpened, setCheckoutOpened] = useState(false);
  const [error, setError] = useState('');

  const formattedFee = Number(fee).toFixed(2);

  const handlePay = async () => {
    if (!token) return;
    setPaying(true);
    setError('');
    try {
      const res = await apiPost('/api/v1/company/reactivation-checkout', {}, token);
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setError('Your session expired. Please log in again.');
        return;
      }
      if (res.status === 422) {
        setError(data?.errors?.reactivation_fee?.[0] || 'Checkout could not be started.');
        return;
      }
      if (!res.ok || !data?.data?.checkout_url) {
        setError('Checkout could not be started. Please try again.');
        return;
      }
      window.open(data.data.checkout_url, '_blank', 'noopener,noreferrer');
      setCheckoutOpened(true);
    } catch {
      setError('Checkout could not be started. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  const handleRefresh = async () => {
    if (!token) return;
    setRefreshing(true);
    setError('');
    try {
      const res = await apiGet('/api/v1/company/reactivation-status', token);
      if (!res.ok) {
        setError('Could not verify payment status. Please try again.');
        return;
      }
      const data = await res.json();
      if (data?.data?.required === false) {
        onCleared();
      } else {
        setError('Payment not detected yet. Complete checkout in the other tab, then try again.');
      }
    } catch {
      setError('Could not verify payment status. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Container className="py-4" data-testid="rwm-reactivation-gate">
      <div className="rwm-reactivation-gate text-center col-lg-8 mx-auto">
        <div className="display-4 text-warning mb-3" aria-hidden="true">
          ⚠
        </div>
        <h1 className="h3 mb-3">Reactivation Fee Required</h1>
        <p className="text-muted mb-4">
          Your account has 3 or more rejected listings. A one-time reactivation fee of €{formattedFee}{' '}
          is required before you can submit new job listings. This fee ensures listing quality on our
          platform.
        </p>

        {error ? (
          <Alert variant="danger" className="text-start" data-testid="rwm-reactivation-error">
            {error}
          </Alert>
        ) : null}

        <div className="d-flex flex-column gap-2 align-items-center">
          <Button
            variant="warning"
            disabled={paying}
            onClick={handlePay}
            data-testid="rwm-reactivation-pay-btn"
          >
            {paying ? 'Starting checkout…' : `Pay Reactivation Fee — €${formattedFee}`}
          </Button>

          <Link
            to="/company/dashboard"
            className="btn btn-link"
            data-testid="rwm-reactivation-back-btn"
          >
            Back to Dashboard
          </Link>
        </div>

        {checkoutOpened ? (
          <div className="mt-4 pt-3 border-top text-start">
            <p className="mb-3">
              Complete payment in the new tab. Return here when done.
            </p>
            <Button
              variant="info"
              disabled={refreshing}
              onClick={handleRefresh}
              data-testid="rwm-reactivation-refresh-btn"
            >
              {refreshing ? 'Checking…' : "I've completed payment — continue"}
            </Button>
          </div>
        ) : null}
      </div>
    </Container>
  );
}
